import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  runTransaction,
  arrayUnion,
  arrayRemove,
  increment,
} from "firebase/firestore";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getRemoteConfig } from "firebase/remote-config";

import firebaseConfig from "@/firebase/config";

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);
const functions = getFunctions(firebaseApp);
const remoteConfig = getRemoteConfig(firebaseApp);
// const messaging = getMessaging(firebaseApp);

class FirebaseService {
  constructor() {
    this.auth = auth;
    this.db = db;
    this.storage = storage;
    this.functions = functions;
    // this.messaging = messaging;
    this.unsubscribeFunctions = [];
  }

  // Authentication Methods
  listenToAuthChanges(callback) {
    const unsubscribe = onAuthStateChanged(this.auth, (user) => {
      callback(user);
    });
    this.unsubscribeFunctions.push(unsubscribe);
    return unsubscribe;
  }

  async signIn(email, password) {
    return signInWithEmailAndPassword(this.auth, email, password);
  }

  async signUp(email, password) {
    return createUserWithEmailAndPassword(this.auth, email, password);
  }

  async logout() {
    try {
      await signOut(this.auth);
    } catch (error) {
      throw error;
    }
  }

  // Firestore Advanced Query Methods

  async getActiveUsersInState(
    stateID = null,
    active = true,
    extraConditions = []
  ) {
    let conditions = [
      { field: "active", operator: "==", value: active },
      { field: "accountType", operator: ">=", value: 2 },
      { field: "state", operator: "==", value: stateID },
    ];
    conditions = conditions.concat(extraConditions);
    try {
      let users = await this.queryCollection("users", conditions);
      return users;
    } catch (error) {
      throw error;
    }
  }

  async queryCollection(
    collectionPath,
    conditions = [],
    orderByField = null,
    limitCount = null,
    startAfterDoc = null
  ) {
    try {
      let queryRef = collection(this.db, collectionPath);

      // Build query with conditions
      if (conditions.length > 0) {
        const whereConditions = conditions.map((condition) =>
          where(condition.field, condition.operator, condition.value)
        );
        queryRef = query(queryRef, ...whereConditions);
      }

      // Add ordering if specified
      if (orderByField) {
        queryRef = query(
          queryRef,
          orderBy(orderByField.field, orderByField.direction)
        );
      }

      // Add limit if specified
      if (limitCount) {
        queryRef = query(queryRef, limit(limitCount));
      }

      // Add pagination if startAfterDoc is provided
      if (startAfterDoc) {
        queryRef = query(queryRef, startAfter(startAfterDoc));
      }

      const snapshot = await getDocs(queryRef);
      return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      throw error;
    }
  }

  // Batch Operations
  async batchOperation(operations) {
    const batch = writeBatch(this.db);

    try {
      operations.forEach((operation) => {
        const docRef = doc(this.db, operation.collection, operation.id);
        switch (operation.type) {
          case "set":
            batch.set(docRef, operation.data, { merge: true });
            break;
          case "update":
            batch.update(docRef, operation.data);
            break;
          case "delete":
            batch.delete(docRef);
            break;
        }
      });

      await batch.commit();
      return true;
    } catch (error) {
      throw error;
    }
  }

  // Transaction Operations
  async runTransaction(callback) {
    try {
      return await runTransaction(this.db, callback);
    } catch (error) {
      throw error;
    }
  }

  // Array Operations
  async updateArray(collectionPath, docId, field, value, operation = "union") {
    try {
      const docRef = doc(this.db, collectionPath, docId);
      const updateData = {
        [field]: operation === "union" ? arrayUnion(value) : arrayRemove(value),
      };
      await updateDoc(docRef, updateData);
      return true;
    } catch (error) {
      throw error;
    }
  }

  // Counter Operations
  async incrementCounter(collectionPath, docId, field, value = 1) {
    try {
      const docRef = doc(this.db, collectionPath, docId);
      await updateDoc(docRef, {
        [field]: increment(value),
      });
      return true;
    } catch (error) {
      throw error;
    }
  }

  // Storage Operations
  async uploadFile(path, file, metadata = {}) {
    try {
      const storageRef = ref(this.storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress =
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            if (metadata.onProgress) {
              metadata.onProgress(progress);
            }
          },
          (error) => reject(error),
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            resolve({ downloadURL, path });
          }
        );
      });
    } catch (error) {
      throw error;
    }
  }

  async deleteFile(path) {
    try {
      const storageRef = ref(this.storage, path);
      await deleteObject(storageRef);
      return true;
    } catch (error) {
      throw error;
    }
  }

  // Cloud Functions
  async callFunction(functionName, data) {
    try {
      const functionRef = httpsCallable(this.functions, functionName);
      const result = await functionRef(data);
      return result.data;
    } catch (error) {
      throw error;
    }
  }

  // Cleanup
  unsubscribeAll() {
    this.unsubscribeFunctions.forEach((unsub) => unsub());
    this.unsubscribeFunctions = [];
  }
}

// Export an instance of FirebaseService
export default FirebaseService;
